import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";

const SERVICES = [
  {
    img: "care_service_1.png",
    name: "Personal Care",
    desc: "Respectful help with bathing, dressing, grooming, and daily hygiene.",
  },
  {
    img: "care_service_2.png",
    name: "Companionship & Outings",
    desc: "A familiar face for conversation, errands, and getting out and about.",
  },
  {
    img: "care_service_3.png",
    name: "Household Support",
    desc: "Light housekeeping, laundry, and tidying to keep home comfortable.",
  },
  {
    img: "care_service_4.png",
    name: "Support for Daily Living",
    desc: "Meal preparation, medication reminders, and everyday routines.",
  },
  {
    img: "care_service_5.png",
    name: "Technology Support",
    desc: "Staying connected with family and care through easy-to-use tools.",
  },
];

const VALUES = [
  {
    icon: "workforce-icon.png",
    title: "An employed care team",
    desc: "Care aides, nursing students, and retired professionals — all employed and supported by iCare Village.",
  },
  {
    icon: "technology-icon.png",
    title: "Technology-enabled care",
    desc: "A connected platform that coordinates your services, schedules, and updates in one place.",
  },
  {
    icon: "house_icon.png",
    title: "Aging at home",
    desc: "Support designed to help you stay in the comfort of your own home for as long as possible.",
  },
  {
    icon: "community-icon.png",
    title: "Connected to community",
    desc: "Care that keeps you supported, independent, and connected within your local community.",
  },
];

const PANELS = ["Welcome", "Support", "Services", "Why iCare Village", "Get started"];

const HomePage = ({ loggedIn }) => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  const handleClick = () => {
    if (loggedIn) {
      navigate("/Services");
    } else {
      navigate("/login");
    }
  };

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const panels = Array.from(root.querySelectorAll(".HM-panel"));

    if (typeof IntersectionObserver === "undefined") {
      panels.forEach((panel) => panel.classList.add("is-visible"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.2) {
            entry.target.classList.add("is-visible");
          }
          if (entry.intersectionRatio >= 0.55) {
            const idx = Number(entry.target.dataset.index);
            if (!Number.isNaN(idx)) setActive(idx);
          }
        });
      },
      { root, threshold: [0.2, 0.55] },
    );

    panels.forEach((panel) => io.observe(panel));
    return () => io.disconnect();
  }, []);

  const goTo = (idx) => {
    const root = scrollRef.current;
    if (!root) return;
    const panel = root.querySelectorAll(".HM-panel")[idx];
    if (!panel) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    panel.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };

  return (
    <div className="HM" ref={scrollRef}>
      <div className="HM-bg" aria-hidden="true" />

      <nav className="HM-dots" aria-label="Section navigation">
        {PANELS.map((label, idx) => (
          <button
            key={label}
            className={`HM-dot ${active === idx ? "is-active" : ""}`}
            aria-label={`Go to ${label}`}
            aria-current={active === idx ? "true" : undefined}
            onClick={() => goTo(idx)}
          />
        ))}
      </nav>

      <section className="HM-panel HM-hero" data-index="0">
        <div className="HM-inner">
          <p className="HM-eyebrow HM-reveal">Home Care</p>
          <h1 className="HM-title HM-reveal" style={{ "--d": "0.06s" }}>
            Compassionate, Respectful, and Affordable care at HOME
          </h1>
          <p className="HM-lede HM-reveal" style={{ "--d": "0.12s" }}>
            iCare Village helps older adults live safely and independently in the
            place they know best — their own home — supported by a care team we
            employ and a platform that keeps everyone connected.
          </p>
          <div className="HM-actions HM-reveal" style={{ "--d": "0.18s" }}>
            <button onClick={handleClick} className="HM-btn HM-btn-primary">
              {loggedIn ? "Book a service today" : "Get started — register today"}
            </button>
            <button
              onClick={() => navigate("/consultation")}
              className="HM-btn HM-btn-ghost"
            >
              Book a free consultation
            </button>
          </div>
        </div>
        <button
          className="HM-scrollcue"
          aria-label="Scroll to next section"
          onClick={() => goTo(1)}
        >
          &darr;
        </button>
      </section>

      <section className="HM-panel HM-support" data-index="1">
        <div className="HM-inner">
          <div className="HM-support-text">
            <h2 className="HM-title HM-reveal">Support Where You Need It</h2>
            <div
              className="HM-support-body HM-reveal"
              style={{ "--d": "0.08s" }}
            >
              <p>
                At iCare Village, we believe that aging should be met with
                dignity, care, and a strong sense of belonging. We provide
                non-medical home care services delivered by our care team – care
                aides, nursing students, and retired professionals – who are
                employed by iCare Village and supported through our
                technology-enabled care platform.
              </p>
              <p>
                Our services are designed to help older adults remain in the
                comfort of their own homes for as long as possible, while staying
                connected, supported, and independent within their communities.
              </p>
            </div>
          </div>

          <aside className="HM-search-card HM-reveal" style={{ "--d": "0.14s" }}>
            <h3 className="HM-search-title">Find Home Care Services Near You</h3>
            <p className="HM-search-lede">
              Enter your postal code or city to view available services in your
              area.
            </p>
            <div className="HM-search-box">
              <label className="HM-visually-hidden" htmlFor="HM-location">
                Postal code or city
              </label>
              <input
                id="HM-location"
                className="HM-search-input"
                type="text"
                placeholder="e.g., Victoria, BC"
              />
              <button
                className="HM-search-btn"
                onClick={() => navigate("/Services")}
              >
                Search services
              </button>
            </div>
          </aside>
        </div>
      </section>

      <section className="HM-panel HM-why" data-index="3">
        <div className="HM-inner">
          <div className="HM-head HM-reveal">
            <h2 className="HM-title">Care you can count on</h2>
            <p className="HM-lede">
              Reliable in-home support, coordinated by people and technology that
              put your well-being first.
            </p>
          </div>
          <div className="HM-value-grid HM-reveal" style={{ "--d": "0.1s" }}>
            {VALUES.map((value) => (
              <article className="HM-value-card" key={value.title}>
                <div className="HM-value-icon">
                  <img
                    src={require(`../images/${value.icon}`)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <h3>{value.title}</h3>
                <p>{value.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="HM-panel HM-close" data-index="4">
        <div className="HM-inner">
          <p className="HM-eyebrow HM-reveal" style={{ justifyContent: "center" }}>
            What guides us
          </p>
          <ul className="HM-values-list HM-reveal" style={{ "--d": "0.06s" }}>
            <li>Compassion</li>
            <li>Respect</li>
            <li>Dignity</li>
            <li>Affordability</li>
          </ul>
          <h2 className="HM-title HM-reveal" style={{ "--d": "0.12s" }}>
            Ready to bring care home?
          </h2>
          <p className="HM-lede HM-reveal" style={{ "--d": "0.18s" }}>
            Book a free consultation to talk through your needs, or explore the
            services available in your community.
          </p>
          <div className="HM-actions HM-reveal" style={{ "--d": "0.24s" }}>
            <button
              className="HM-btn HM-btn-primary"
              onClick={() => navigate("/consultation")}
            >
              Book a free consultation
            </button>
            <button
              className="HM-btn HM-btn-ghost"
              onClick={() => navigate("/Services")}
            >
              Explore our services
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
